import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { renderHanziSheetPage, getPageCount, getHanziSheetSize } from '../utils/renderer'
import { loadRemoteStrokeOrderData } from '../utils/stroke-loader'
import { loadSubsetFont, getActiveFontFamily } from '../utils/font-loader'
import type { HanziConfig } from '../types'

interface PreviewAreaProps {
  config?: HanziConfig
}

const defaultConfig: HanziConfig = {
  chars: '我爱学习汉字',
  gridType: 'mi',
  traceCount: 3,
  insertBlankRow: false,
  insertBlankCol: false,
  showStrokeOrder: false,
  traceColor: '#E74C3C',
  lineColor: '#9E9E9E',
  paperSize: 'a4',
  fontFamily: 'wenkai',
  showPinyin: false,
  inputMode: 'char',
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 3.0
const ZOOM_STEP = 0.25

const PreviewArea: React.FC<PreviewAreaProps> = ({ config = defaultConfig }) => {
  const { t } = useTranslation()
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [fitToScreen, setFitToScreen] = useState(false)
  const [zoom, setZoom] = useState(1.2)

  const pageCount = getPageCount(config)
  const { width: paperWidth, height: paperHeight } = getHanziSheetSize(config.paperSize)
  const isPinyinAbove = config.showPinyin

  const aspectRatio = paperWidth / paperHeight
  const BASE_WIDTH = 595
  const BASE_HEIGHT = Math.round(BASE_WIDTH / aspectRatio)

  const handleFitToggle = useCallback(() => {
    setFitToScreen((prev) => !prev)
  }, [])

  const handleZoomIn = useCallback(() => {
    setFitToScreen(false)
    setZoom((prev) => Math.min(MAX_ZOOM, Math.round((prev + ZOOM_STEP) * 100) / 100))
  }, [])

  const handleZoomOut = useCallback(() => {
    setFitToScreen(false)
    setZoom((prev) => Math.max(MIN_ZOOM, Math.round((prev - ZOOM_STEP) * 100) / 100))
  }, [])

  const handleZoomReset = useCallback(() => {
    setFitToScreen(false)
    setZoom(1)
  }, [])

  useEffect(() => {
    const loadAndRender = async () => {
      if (!config.chars || config.chars.length === 0) return

      setLoading(true)
      try {
        await loadSubsetFont(config.chars)

        if (config.inputMode === 'char' && config.showStrokeOrder) {
          try {
            await loadRemoteStrokeOrderData(config.chars)
          } catch (err) {
            console.warn('Stroke data load failed:', err)
          }
        }

        const fontFamily = getActiveFontFamily()

        for (let i = 0; i < pageCount; i++) {
          const canvas = canvasRefs.current[i]
          if (!canvas) continue
          const ctx = canvas.getContext('2d')
          if (!ctx) continue
          renderHanziSheetPage(ctx, config, i, fontFamily)
        }
      } catch (err) {
        console.warn('Preview render failed:', err)
      } finally {
        setLoading(false)
      }
    }

    loadAndRender()
  }, [config, pageCount])

  const previewScale = isPinyinAbove ? 1.04 : 1
  const previewWidth = fitToScreen ? '100%' : `${BASE_WIDTH * zoom * previewScale}px`
  const previewHeight = fitToScreen ? 'auto' : `${BASE_HEIGHT * zoom * previewScale}px`

  return (
    <div className="flex flex-col items-center h-full">
      <div className="flex items-center justify-between w-full max-w-3xl mb-3 flex-shrink-0 gap-2">
        <h3 className="text-lg font-semibold text-text">{t('preview.title')}</h3>

        <div className="flex items-center gap-2">
          {loading && (
            <span className="text-xs text-text-light animate-pulse hidden sm:inline">{t('preview.loading')}</span>
          )}

          <div className="flex items-center bg-white border border-border rounded-lg overflow-hidden">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= MIN_ZOOM && !fitToScreen}
              className="px-2 py-1.5 text-text-secondary hover:bg-primary/5 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={t('preview.zoomOut')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>

            <span className="px-1 text-xs font-medium text-text text-center tabular-nums whitespace-nowrap">
              {fitToScreen ? t('preview.fit') : `${Math.round(zoom * 100)}%`}
            </span>

            <button
              onClick={handleZoomIn}
              disabled={zoom >= MAX_ZOOM && !fitToScreen}
              className="px-2 py-1.5 text-text-secondary hover:bg-primary/5 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={t('preview.zoomIn')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          <button
            onClick={handleZoomReset}
            disabled={fitToScreen || zoom === 1}
            className="px-2 py-1.5 text-xs font-medium text-text-secondary border border-border rounded-lg hover:bg-primary/5 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title={t('preview.resetZoom')}
          >
            1:1
          </button>

          <button
            onClick={handleFitToggle}
            className={`px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
              fitToScreen
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border text-text-secondary hover:border-primary hover:text-primary'
            }`}
          >
            {fitToScreen ? t('preview.actualSize') : t('preview.fit')}
          </button>

          <button
            onClick={() => {
              const el = scrollContainerRef.current
              if (!el) return
              if (!document.fullscreenElement) {
                el.requestFullscreen().catch(() => {})
              } else {
                document.exitFullscreen().catch(() => {})
              }
            }}
            className="px-2 py-1.5 text-xs font-medium text-text-secondary border border-border rounded-lg hover:bg-primary/5 hover:text-primary transition-colors"
            title={t('preview.fullscreen')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className={`flex-1 w-full overflow-y-auto ${fitToScreen ? '' : 'overflow-auto'}`}
        style={
          fitToScreen
            ? {}
            : {
                backgroundImage:
                  'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
                backgroundSize: '16px 16px',
              }
        }
      >
        <div className={`flex flex-col items-center ${isPinyinAbove ? 'gap-6 py-6' : 'gap-4 py-4'}`}>
          {pageCount > 0 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <div
                key={i}
                className="bg-white shadow-xl rounded-sm overflow-hidden transition-all duration-300 ease-out"
                style={
                  fitToScreen
                    ? {
                        width: 'calc(100% - 32px)',
                        maxWidth: `${paperWidth}px`,
                        aspectRatio: `${paperWidth} / ${paperHeight}`,
                      }
                    : {
                        width: previewWidth,
                        height: previewHeight,
                        minWidth: previewWidth,
                        minHeight: previewHeight,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                      }
                }
              >
                <canvas
                  ref={(el) => { canvasRefs.current[i] = el }}
                  width={paperWidth}
                  height={paperHeight}
                  className="w-full h-full block"
                />
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center h-64 text-text-light">
              {t('input.placeholder')}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mt-2 mb-1 flex-shrink-0">
        <p className="text-xs text-text-light tabular-nums">
          {pageCount > 0
            ? t('preview.page', { current: 1, total: pageCount })
            : t('preview.page', { current: 0, total: 0 })}
        </p>
        <span className="text-border">|</span>
        <p className="text-xs text-text-light tabular-nums">
          {config.paperSize === 'letter' ? 'Letter 216 × 279 mm' : 'A4 210 × 297 mm'}
        </p>
      </div>
    </div>
  )
}

export default PreviewArea
